import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus,
  Trash2,
  ChevronDown,
  Check,
  X,
  Paperclip,
  ArrowLeft,
} from "lucide-react";
import {
  getQualityMeta,
  createQualityMeta,
  updateQualityMeta,
} from "../../../api/quality/meta";
import MobileAttachmentBox from "../../../components/common/papers/MobileAttachmentBox";

export default function QualityCheckListNew() {
  // URL 파라미터에서 프로젝트 ID와 문서 DNS 추출
  const { id: routeProjectId, dns: doc_dns } = useParams();
  const navigate = useNavigate();
  const attachmentRef = useRef(null);

  const projectId =
    routeProjectId ||
    localStorage.getItem("projectId") ||
    localStorage.getItem("projectCode") ||
    "";

  // ------------------------------
  // 상태 관리
  // ------------------------------
  const [dns, setDns] = useState(doc_dns || "");
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    doc_date: new Date().toISOString().slice(0, 10),
    status: "draft",
    result: "",
    title: "",
    note: "",
  });

  const [processMeta, setProcessMeta] = useState({
    process_name: "",
    lot_no: "",
    inspector: "",
    reviewer: "",
    approver: "",
    method: "",
    acceptance_criteria: "",
    place: "",
  });

  const [items, setItems] = useState([
    { item: "", criteria: "", result: "", remark: "" },
  ]);

  // 섹션 접힘 상태
  const [expandedSections, setExpandedSections] = useState({
    docInfo: true,
    processMeta: true,
    inspection: true,
    note: false,
    attachment: true,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ------------------------------
  // 초기화 및 데이터 로드
  // ------------------------------

  // 로그인 사용자명을 검사자에 보조 입력
  useEffect(() => {
    const n = localStorage.getItem("userName") || "";
    if (!processMeta.inspector && n) {
      setProcessMeta((p) => ({ ...p, inspector: n }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 기존 문서 로드
  useEffect(() => {
    if (!doc_dns) return;
    async function loadDetail() {
      try {
        const res = await getQualityMeta({ dns: doc_dns });
        const meta = res?.meta || {};

        if (meta.form) {
          setForm({
            doc_date: meta.form.doc_date || "",
            status: meta.form.status || "draft",
            result: meta.form.result || "",
            title: meta.form.title || "",
            note: meta.form.note || "",
          });
        }

        if (meta.processMeta) {
          setProcessMeta(meta.processMeta);
        }

        if (meta.items) {
          setItems(meta.items);
        }
      } catch (err) {
        console.error(err);
        alert("공정검사 문서를 불러오지 못했습니다.");
      }
    }

    loadDetail();
  }, [doc_dns]);

  // ------------------------------
  // 핸들러
  // ------------------------------
  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onChangeMeta = (k, v) => setProcessMeta((p) => ({ ...p, [k]: v }));

  const addRow = () =>
    setItems((p) => [...p, { item: "", criteria: "", result: "", remark: "" }]);

  const removeRow = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const updateRow = (i, k, v) =>
    setItems((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  // 자동 제목 생성
  const autoTitle = useMemo(() => {
    const p = processMeta.process_name?.trim();
    const l = processMeta.lot_no?.trim();
    if (p && l) return `[공정] ${p} - ${l}`;
    if (p) return `[공정] ${p}`;
    return "";
  }, [processMeta]);

  // 요약 통계
  const summary = useMemo(() => {
    const total = items.length;
    const pass = items.filter((r) => r.result === "pass").length;
    const fail = items.filter((r) => r.result === "fail").length;
    const passRate = total > 0 ? Math.round((pass / total) * 100) : 0;
    return { total, pass, fail, passRate };
  }, [items]);

  // 자동 판정 (하나라도 불합격이면 전체 불합격)
  const derivedDocResult = useMemo(() => {
    if (form.result) return form.result; // 수동 오버라이드 값이 있다면 우선
    if (summary.fail > 0) return "fail";
    if (summary.total > 0 && summary.pass === summary.total) return "pass";
    return "";
  }, [form.result, summary]);

  // 저장 처리
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const body = {
        layout: null,
        values: {
          project: projectId,
          module: "quality",
          doc_type: "PC", // Process Checklist
          dns,
          meta: {
            form: {
              ...form,
              title: form.title?.trim() || autoTitle,
              result: derivedDocResult,
            },
            processMeta,
            items,
            summary,
            __client__: { form_type: "PC" },
          },
        },
      };

      let result;
      if (dns) {
        result = await updateQualityMeta(dns, body.values);
      } else {
        result = await createQualityMeta(body.values);
      }

      const newDns = result?.dns || result?.doc_dns || dns;

      // 첨부파일 저장
      if (newDns && attachmentRef.current) {
        await attachmentRef.current.save(newDns);
      }

      if (newDns) {
        setDns(newDns);
        alert("저장되었습니다.");
        // 상세 보기 화면으로 이동
        navigate(
          `/project/${projectId}/quality/view/process/${encodeURIComponent(
            newDns
          )}`
        );
      }
    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => navigate(-1);

  // ------------------------------
  // 렌더링
  // ------------------------------
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* 모바일 헤더 */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="p-1 -ml-1 text-gray-600">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold text-gray-900">공정검사 기록</h1>
        </div>
        <div className="text-xs font-medium px-2 py-1 bg-blue-50 text-blue-600 rounded">
          {form.status === "draft" ? "작성중" : "완료"}
        </div>
      </header>

      {/* 본문 영역 */}
      <main className="flex-1 px-4 pt-4 pb-24 overflow-y-auto">
        {/* 요약 카드 */}
        <div className="mb-4 p-4 rounded-2xl bg-white shadow-sm border border-blue-100/50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-gray-800">검사 현황</span>
            <div className="flex items-center gap-2">
              {derivedDocResult && (
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    derivedDocResult === "pass"
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {derivedDocResult === "pass" ? "적합" : "부적합"}
                </span>
              )}
              <span className="text-xs text-gray-400">{form.doc_date}</span>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <SummaryBox
              label="총 항목"
              value={summary.total}
              color="text-gray-800"
            />
            <SummaryBox
              label="합격"
              value={summary.pass}
              color="text-green-600"
            />
            <SummaryBox
              label="불합격"
              value={summary.fail}
              color="text-red-500"
            />
            <SummaryBox
              label="합격률"
              value={`${summary.passRate}%`}
              color="text-blue-600"
            />
          </div>
        </div>

        <div className="space-y-3">
          {/* 1. 문서 정보 */}
          <Section
            title="문서 정보"
            expanded={expandedSections.docInfo}
            onToggle={() => toggleSection("docInfo")}
          >
            <InputField
              label="제목"
              value={form.title}
              onChange={(v) => onChange("title", v)}
              placeholder={autoTitle || "자동 생성됨"}
            />
            <InputField
              label="검사일"
              type="date"
              value={form.doc_date}
              onChange={(v) => onChange("doc_date", v)}
            />
            <div>
              <label className="text-gray-500 text-xs font-medium mb-1.5 block ml-1">
                상태
              </label>
              <select
                value={form.status}
                onChange={(e) => onChange("status", e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none"
              >
                <option value="draft">작성중</option>
                <option value="pending">검토요청</option>
                <option value="approved">승인완료</option>
                <option value="rejected">반려됨</option>
              </select>
            </div>
          </Section>

          {/* 2. 공정 정보 */}
          <Section
            title="공정 정보"
            expanded={expandedSections.processMeta}
            onToggle={() => toggleSection("processMeta")}
          >
            <InputField
              label="공정명"
              value={processMeta.process_name}
              onChange={(v) => onChangeMeta("process_name", v)}
              placeholder="예: 철근 배근, 거푸집 설치"
              required
            />
            <InputField
              label="LOT/Batch"
              value={processMeta.lot_no}
              onChange={(v) => onChangeMeta("lot_no", v)}
              placeholder="위치 또는 식별번호"
              required
            />
            <InputField
              label="검사 장소"
              value={processMeta.place}
              onChange={(v) => onChangeMeta("place", v)}
              placeholder="현장 위치"
            />
            <InputField
              label="검사 방법"
              value={processMeta.method}
              onChange={(v) => onChangeMeta("method", v)}
              placeholder="예: 육안검사, 실측"
            />
            <InputField
              label="합격 기준"
              value={processMeta.acceptance_criteria}
              onChange={(v) => onChangeMeta("acceptance_criteria", v)}
              placeholder="도면 및 시방서 기준"
            />
          </Section>

          {/* 3. 검사 항목 */}
          <Section
            title="검사 항목"
            expanded={expandedSections.inspection}
            onToggle={() => toggleSection("inspection")}
            badge={`${summary.total} 항목`}
          >
            <div className="space-y-4">
              {items.map((row, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-3"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-gray-500">
                      NO. {idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <input
                    type="text"
                    value={row.item}
                    onChange={(e) => updateRow(idx, "item", e.target.value)}
                    placeholder="검사 항목 내용"
                    className="w-full bg-transparent border-b border-gray-200 py-1 text-sm font-medium placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={row.criteria}
                    onChange={(e) => updateRow(idx, "criteria", e.target.value)}
                    placeholder="판정 기준"
                    className="w-full bg-transparent border-b border-gray-200 py-1 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                  />

                  {/* 판정 버튼 그룹 */}
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => updateRow(idx, "result", "pass")}
                      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-bold transition-all ${
                        row.result === "pass"
                          ? "bg-green-500 text-white shadow-md shadow-green-200"
                          : "bg-white border border-gray-200 text-gray-400"
                      }`}
                    >
                      <Check size={16} /> 적합
                    </button>
                    <button
                      type="button"
                      onClick={() => updateRow(idx, "result", "fail")}
                      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-bold transition-all ${
                        row.result === "fail"
                          ? "bg-red-500 text-white shadow-md shadow-red-200"
                          : "bg-white border border-gray-200 text-gray-400"
                      }`}
                    >
                      <X size={16} /> 부적합
                    </button>
                  </div>

                  <input
                    type="text"
                    value={row.remark}
                    onChange={(e) => updateRow(idx, "remark", e.target.value)}
                    placeholder="비고 / 측정값 기록"
                    className="w-full bg-white px-3 py-2 rounded-lg border border-gray-200 text-xs focus:border-gray-400 focus:outline-none"
                  />
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addRow}
              className="w-full mt-2 flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-blue-300 bg-blue-50/50 text-blue-600 font-medium text-sm hover:bg-blue-50 transition-colors"
            >
              <Plus size={16} />
              항목 추가하기
            </button>
          </Section>

          {/* 4. 종합 의견 */}
          <Section
            title="종합 의견"
            expanded={expandedSections.note}
            onToggle={() => toggleSection("note")}
          >
            <textarea
              value={form.note}
              onChange={(e) => onChange("note", e.target.value)}
              placeholder="종합 의견 및 특이사항을 입력하세요."
              rows={3}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm focus:border-blue-500 focus:outline-none resize-none"
            />
          </Section>

          {/* 5. 서명 정보 */}
          <Section title="서명 정보" expanded={true} onToggle={() => {}}>
            <div className="grid grid-cols-3 gap-2">
              <InputField
                label="검사자"
                value={processMeta.inspector}
                onChange={(v) => onChangeMeta("inspector", v)}
              />
              <InputField
                label="검토자"
                value={processMeta.reviewer}
                onChange={(v) => onChangeMeta("reviewer", v)}
              />
              <InputField
                label="승인자"
                value={processMeta.approver}
                onChange={(v) => onChangeMeta("approver", v)}
              />
            </div>
          </Section>

          {/* 6. 첨부자료 */}
          <Section
            title="사진 및 첨부"
            expanded={expandedSections.attachment}
            onToggle={() => toggleSection("attachment")}
            icon={<Paperclip size={16} className="text-gray-500" />}
          >
            <MobileAttachmentBox ref={attachmentRef} />
          </Section>
        </div>
      </main>

      {/* 하단 고정 액션바 */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 pb-safe z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <div className="flex gap-3 max-w-screen-md mx-auto">
          <button
            type="button"
            className="flex-1 h-12 rounded-xl border border-gray-200 bg-gray-50 text-gray-700 font-bold text-sm hover:bg-gray-100 transition-colors"
            onClick={handleBack}
          >
            취소
          </button>
          <button
            type="button"
            className="flex-[2] h-12 rounded-xl bg-blue-600 text-white font-bold text-sm shadow-lg shadow-blue-200 hover:bg-blue-700 disabled:opacity-50 transition-all"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "저장 중..." : "저장하기"}
          </button>
        </div>
      </footer>
    </div>
  );
}

// ------------------------------------------------
// Sub Components
// ------------------------------------------------

function SummaryBox({ label, value, color }) {
  return (
    <div className="flex flex-col items-center justify-center py-2 rounded-xl bg-gray-50 border border-gray-100">
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      <div className="text-[10px] text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function Section({ title, expanded, onToggle, badge, icon, children }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white overflow-hidden transition-all duration-200">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3.5 bg-white active:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-bold text-sm text-gray-800">{title}</h3>
          {badge && (
            <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-[10px] font-bold">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>
      {expanded && (
        <div className="px-4 pb-5 pt-1 space-y-3 border-t border-gray-100 animate-fadeIn">
          {children}
        </div>
      )}
    </section>
  );
}

function InputField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  disabled,
  required,
}) {
  return (
    <div>
      <label className="text-gray-500 text-xs font-medium mb-1.5 block ml-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm placeholder-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all disabled:bg-gray-50"
      />
    </div>
  );
}
